import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  getStorageLocations,
  createStorageLocation,
  updateStorageLocation,
  deleteStorageLocation,
  setDatabaseStorageLocation,
} from "../../api/adminApi";

const KINDS = ["FileSystem", "SharedDrive", "AzureBlob", "S3"];
const CLOUD_KINDS = new Set(["AzureBlob", "S3"]);
const emptyForm = { id: 0, name: "", kind: "FileSystem", rootPath: "", endpoint: "", credentialRef: "", tier: "Hot", enabled: true };

// Per-backend guidance. `rootKey`/`descKey`/`epKey` resolve to storage.locations.* i18n
// strings; placeholders are literal technical examples (not translated). `needsEndpoint`
// shows the endpoint + credential fields for cloud backends.
const KIND_META = {
  FileSystem:  { rootKey: "rootFs",    rootPh: "/var/depository/archive",            descKey: "descFs",    needsEndpoint: false },
  SharedDrive: { rootKey: "rootShare", rootPh: "\\\\nas\\depository\\archive",        descKey: "descShare", needsEndpoint: false },
  AzureBlob:   { rootKey: "rootAzure", rootPh: "archives",                            descKey: "descAzure", needsEndpoint: true,  epKey: "epAzure", epPh: "https://account.blob.core.windows.net" },
  S3:          { rootKey: "rootS3",    rootPh: "my-archive-bucket",                   descKey: "descS3",    needsEndpoint: true,  epKey: "epS3",    epPh: "eu-central-1  (or https://minio.local:9000)" },
};

// Manage the StorageLocation registry (create/edit/delete) and, when opened in the
// context of a selected database, bind/unbind that database to a location.
export default function StorageLocationsModal({
  token,
  onClose,
  selectedDatabaseId = null,
  selectedDatabaseName = "",
  currentStorageLocationId = null,
  onBindingChanged,
}) {
  const { t } = useTranslation();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [bindValue, setBindValue] = useState(currentStorageLocationId ?? "");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getStorageLocations(token);
      setLocations(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => { setForm(emptyForm); setEditing(false); setShowForm(false); };

  function startAdd() { setForm(emptyForm); setEditing(false); setError(""); setInfo(""); setShowForm(true); }

  async function handleSave(e) {
    e.preventDefault();
    setError(""); setInfo("");
    const payload = { ...form, name: form.name.trim(), rootPath: form.rootPath.trim() };
    if (!payload.name || !payload.rootPath) { setError(t("storage.locations.required")); return; }
    try {
      if (editing) await updateStorageLocation(token, payload);
      else await createStorageLocation(token, payload);
      resetForm();
      await load();
      setInfo(t("storage.locations.saved"));
    } catch (e) {
      setError(e?.message || String(e));
    }
  }

  function startEdit(loc) {
    setEditing(true);
    setShowForm(true);
    setInfo("");
    setForm({
      id: loc.id, name: loc.name, kind: loc.kind, rootPath: loc.rootPath,
      endpoint: loc.endpoint || "", credentialRef: loc.credentialRef || "",
      tier: loc.tier || "Hot", enabled: loc.enabled,
    });
  }

  async function handleDelete(loc) {
    if (!window.confirm(t("storage.locations.deleteConfirm", { name: loc.name }))) return;
    setError(""); setInfo("");
    try {
      await deleteStorageLocation(token, loc.id);
      await load();
    } catch (e) {
      setError(e?.message || String(e));
    }
  }

  async function handleBind(e) {
    const raw = e.target.value;
    setBindValue(raw);
    setError(""); setInfo("");
    const locationId = raw === "" ? null : Number(raw);
    try {
      await setDatabaseStorageLocation(token, selectedDatabaseId, locationId);
      setInfo(t("storage.locations.bound", { db: selectedDatabaseName }));
      if (onBindingChanged) onBindingChanged(locationId);
    } catch (e) {
      // Surface the server guard (e.g. STORAGE_CLOUD_READ_PENDING) verbatim, then revert the select.
      setError(e?.message || String(e));
      setBindValue(currentStorageLocationId ?? "");
    }
  }

  const meta = KIND_META[form.kind] || KIND_META.FileSystem;
  const isCloud = CLOUD_KINDS.has(form.kind);

  return (
    <div className="dbModalOverlay" onClick={onClose}>
      <div className="dbModalCard createDbModalCard" onClick={(e) => e.stopPropagation()}>
        <button className="drawer-close-btn drawer-close-btn-abs" onClick={onClose} aria-label={t("common.close")}>×</button>
        <div className="dbModalIcon" style={{ color: "#c47d00" }}>🗄</div>
        <div className="dbModalTitle">{t("storage.locations.title")}</div>
        <div className="dbModalDesc">{t("storage.locations.subtitle")}</div>

        {error ? <div className="arch-alert arch-alert--error" role="alert">{error}</div> : null}
        {info ? <div className="arch-alert arch-alert--success">{info}</div> : null}

        {selectedDatabaseId ? (
          <div className="sl-bind-card">
            <label className="createDbLabel" htmlFor="sl-bind-select">
              {t("storage.locations.bindLabel", { db: selectedDatabaseName })}
            </label>
            <select id="sl-bind-select" className="createDbInput" value={bindValue} onChange={handleBind}>
              <option value="">{t("storage.locations.bindDefault")}</option>
              {locations.filter((l) => l.enabled).map((l) => (
                <option key={l.id} value={l.id}>{l.name} ({l.kind})</option>
              ))}
            </select>
            <div className="sl-hint">{t("storage.locations.cloudReadPending")}</div>
          </div>
        ) : null}

        <div className="sl-section-title">{t("storage.locations.registry")}</div>
        {loading ? (
          <div className="sl-muted">{t("common.loading")}</div>
        ) : locations.length === 0 ? (
          <div className="sl-muted">{t("storage.locations.empty")}</div>
        ) : (
          <div className="sl-list">
            {locations.map((l) => (
              <div key={l.id} className={`sl-row${l.enabled ? "" : " sl-row--disabled"}`}>
                <div className="sl-row-main">
                  <span className="sl-row-name">{l.name}</span>
                  <span className="sl-badge">{l.kind}</span>
                  <span className="sl-badge sl-badge--tier">{l.tier}</span>
                </div>
                <div className="sl-row-path">{l.rootPath}</div>
                <div className="sl-row-actions">
                  <button className="link-btn" onClick={() => startEdit(l)}>{t("storage.locations.edit")}</button>
                  <button className="link-btn sl-danger" onClick={() => handleDelete(l)}>{t("common.remove")}</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!showForm ? (
          <div className="sl-form-actions" style={{ marginTop: 16 }}>
            <button type="button" className="primary-btn" onClick={startAdd}>
              + {t("storage.locations.addTitle")}
            </button>
          </div>
        ) : (
        <>
        <div className="sl-section-title">{editing ? t("storage.locations.editTitle") : t("storage.locations.addTitle")}</div>
        <form onSubmit={handleSave} className="sl-form">
          <div className="sl-field">
            <label className="createDbLabel">{t("storage.locations.name")}</label>
            <input className="createDbInput" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div className="sl-field">
            <label className="createDbLabel">{t("storage.locations.kind")}</label>
            <select className="createDbInput" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
          <div className="sl-field sl-field--full sl-desc">{t(`storage.locations.${meta.descKey}`)}</div>
          <div className="sl-field sl-field--full">
            <label className="createDbLabel">{t(`storage.locations.${meta.rootKey}`)}</label>
            <input className="createDbInput" value={form.rootPath} placeholder={meta.rootPh}
                   onChange={(e) => setForm({ ...form, rootPath: e.target.value })} required />
            <div className="sl-hint">{t(`storage.locations.${meta.rootKey}Hint`)}</div>
          </div>
          {isCloud ? (
            <>
              <div className="sl-field sl-field--full">
                <label className="createDbLabel">{t(`storage.locations.${meta.epKey}`)}</label>
                <input className="createDbInput" value={form.endpoint} placeholder={meta.epPh}
                       onChange={(e) => setForm({ ...form, endpoint: e.target.value })} />
                <div className="sl-hint">{t(`storage.locations.${meta.epKey}Hint`)}</div>
              </div>
              <div className="sl-field sl-field--full">
                <label className="createDbLabel">{t("storage.locations.credentialRef")} <span className="sl-optional">({t("storage.locations.optional")})</span></label>
                <input className="createDbInput" value={form.credentialRef}
                       onChange={(e) => setForm({ ...form, credentialRef: e.target.value })} placeholder="DepositoryStorageConnString" />
                <div className="sl-hint">{t("storage.locations.credentialRefHint")}</div>
              </div>
            </>
          ) : null}
          <div className="sl-field">
            <label className="createDbLabel">{t("storage.locations.tier")}</label>
            <select className="createDbInput" value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })}>
              <option value="Hot">{t("storage.locations.tierHot")}</option>
              <option value="Cold">{t("storage.locations.tierCold")}</option>
            </select>
          </div>
          {editing ? (
            <label className="sl-check">
              <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
              {t("storage.locations.enabled")}
            </label>
          ) : null}
          <div className="sl-form-actions">
            <button type="submit" className="primary-btn">{editing ? t("common.save") : t("storage.locations.add")}</button>
            <button type="button" className="dbModalCancel" onClick={resetForm}>{t("common.cancel")}</button>
          </div>
        </form>
        </>
        )}
      </div>
    </div>
  );
}
